import styled from '@xstyled/styled-components';
import React from 'react';
import { t } from 'ttag';
import { select } from '~/lib/select';
import { Button } from '~/ui/buttons/Button';
import { Flex } from '~/ui/flex/Flex';
import Icon from '../assets/pass_fail.svg';
import DarkThemeIcon from '../assets/pass_fail_dark.svg';
import { TrainingState, useTrainingStore } from '../../state';
import { OnboardingStages, useOnboardingStore } from '../Store';
import { useNextSequence } from '~/training/useNextSequence';
import useIsDarkMode from '~/lib/themes/hooks/useIsDarkMode';

const PassFailIcon = styled(Icon)`
  fill: #e0e0e0;
  margin-bottom: -30px;
  margin-top: -30px;
`;

const DarkThemePassFailIcon = styled(DarkThemeIcon)`
  fill: #e0e0e0;
  margin-bottom: -30px;
  margin-top: -30px;
`;

const SmallText = styled.small`
  b {
    font-family: ralewayBold;
  }
`;

const ThemeAwarePassFailIcon = () => {
  const isDarkMode = useIsDarkMode();

  return isDarkMode ? <DarkThemePassFailIcon /> : <PassFailIcon />;
};

const selector = (s: TrainingState) =>
  select(
    s,
    'disclaimer',
    'onboarding',
    'scorePassingPercent',
    'attemptLimit',
    'attemptsRemaining'
  );

// Attempts are unlimited unless the API sends both numbers (attempt_limits flag).
const AttemptsNote = ({
  attemptLimit,
  attemptsRemaining
}: Pick<TrainingState, 'attemptLimit' | 'attemptsRemaining'>) => {
  if (attemptLimit == null || attemptsRemaining == null) {
    return (
      <SmallText>
        {t`You can retake it if needed — so take your time and do your best!`}
      </SmallText>
    );
  }

  if (attemptsRemaining === 0) {
    return (
      <SmallText>
        {t`You have no attempts left. Please contact your administrator.`}
      </SmallText>
    );
  }

  return (
    <SmallText>
      <b>{t`Attempts left: ${attemptsRemaining} of ${attemptLimit}`}</b>
      <br />
      {t`Take your time and do your best!`}
    </SmallText>
  );
};

export const PassFail = () => {
  const { stage, next } = useOnboardingStore();
  const { onboarding, scorePassingPercent, attemptLimit, attemptsRemaining } =
    useTrainingStore(selector);
  const nextSequence = useNextSequence();

  const GoNext = () => {
    if (onboarding) {
      return next();
    } else {
      nextSequence().then();
    }
  };

  return (
    <Flex
      variant="list"
      justifyContent="space-between"
      data-test-id="pass-fail-card"
    >
      <h2>{t`Pass/Fail`}</h2>
      <Flex variant="center" w="auto" h="auto">
        <ThemeAwarePassFailIcon />
      </Flex>
      <SmallText
        dangerouslySetInnerHTML={{
          __html: t`To complete this course, you'll need to score <b>${scorePassingPercent}% or higher</b> on assessments.`
        }}
      />
      <AttemptsNote
        attemptLimit={attemptLimit}
        attemptsRemaining={attemptsRemaining}
      />
      <Button
        onClick={GoNext}
        disabled={stage !== OnboardingStages.PassFail}
        aria-hidden={stage !== OnboardingStages.PassFail}
        data-test-id="next-button"
      >
        {t`Next`}
      </Button>
    </Flex>
  );
};
